import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Currency {
	code: string;
	name: string;
	flag: string;
}

export interface ExchangeRate {
	rate: number;
	convertedAmount: number;
}

export interface CurrencyDetail {
	countryName: string;
	currency: string;
	symbol: string;
	exchangeRate: ExchangeRate;
}

export interface CurrencyConverterState {
	amount: number;
	fromCurrency: Currency;
	toCurrency: Currency;
	loading: boolean;
	conversion: any;
	rate: number | null;
	timestamp: string;
	error: string;
	selectedCountryIndex: number;
	details: CurrencyDetail[];
	debouncedAmount: number;
}

const initialCurrencies: Currency[] = [
	{
		code: "NGN",
		name: "Nigerian Naira",
		flag: "https://flagcdn.com/ng.svg",
	},
	{
		code: "USD",
		name: "US Dollar",
		flag: "https://flagcdn.com/us.svg",
	},
	{
		code: "EUR",
		name: "Euro",
		flag: "https://flagcdn.com/eu.svg",
	},
	{
		code: "GBP",
		name: "British Pound",
		flag: "https://flagcdn.com/gb.svg",
	},
	{
		code: "CAD",
		name: "Canadian Dollar",
		flag: "https://flagcdn.com/ca.svg",
	},
	{
		code: "AUD",
		name: "Australian Dollar",
		flag: "https://flagcdn.com/au.svg",
	},
];

const initialState: CurrencyConverterState = {
	amount: 100,
	fromCurrency: initialCurrencies[0],
	toCurrency: initialCurrencies[1],
	loading: false,
	conversion: null,
	rate: null,
	timestamp: "",
	error: "",
	selectedCountryIndex: -1,
	details: [],
	debouncedAmount: 100,
};

const currencyConverterSlice = createSlice({
	name: "currencyConverter",
	initialState,
	reducers: {
		setAmount: (state, action: PayloadAction<number>) => {
			state.amount = action.payload;
		},
		setFromCurrency: (state, action: PayloadAction<Currency>) => {
			state.fromCurrency = action.payload;
			state.error = "";
		},
		setToCurrency: (state, action: PayloadAction<Currency>) => {
			state.toCurrency = action.payload;
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setConversion: (state, action: PayloadAction<any>) => {
			state.conversion = action.payload;
		},
		setRate: (state, action: PayloadAction<number | null>) => {
			state.rate = action.payload;
		},
		setTimestamp: (state, action: PayloadAction<string>) => {
			state.timestamp = action.payload;
		},
		setError: (state, action: PayloadAction<string>) => {
			state.error = action.payload;
		},
		setSelectedCountryIndex: (state, action: PayloadAction<number>) => {
			state.selectedCountryIndex = action.payload;
		},
		setDetails: (state, action: PayloadAction<CurrencyDetail[]>) => {
			state.details = action.payload;
		},
		setDebouncedAmount: (state, action: PayloadAction<number>) => {
			state.debouncedAmount = action.payload;
		},
		clearError: (state) => {
			state.error = "";
		},
		resetConverter: (state) => {
			state.amount = 100;
			state.fromCurrency = initialCurrencies[0];
			state.toCurrency = initialCurrencies[1];
			state.loading = false;
			state.conversion = null;
			state.rate = null;
			state.timestamp = "";
			state.error = "";
			state.selectedCountryIndex = -1;
			state.details = [];
			state.debouncedAmount = 100;
		},
	},
});

export const {
	setAmount,
	setFromCurrency,
	setToCurrency,
	setLoading,
	setConversion,
	setRate,
	setTimestamp,
	setError,
	setSelectedCountryIndex,
	setDetails,
	setDebouncedAmount,
	clearError,
	resetConverter,
} = currencyConverterSlice.actions;

export default currencyConverterSlice.reducer;
